import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", short_description: "", image_path: "", sort_order: 0, active: true };

export default function Qualities() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/qualities");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (q) => {
    setEdit(q);
    setForm({
      name: q.name,
      short_description: q.short_description || "",
      image_path: q.image_path || "",
      sort_order: q.sort_order || 0,
      active: q.active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Kalite adı zorunlu"); return; }
    try {
      if (edit) await api.put(`/admin/qualities/${edit.id}`, form);
      else await api.post("/admin/qualities", form);
      toast.success("Kaydedildi");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/qualities/${delId}`);
      toast.success("Silindi");
      setDelId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
      setDelId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-qualities-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-tag">Kategori</span>
          <h1 className="font-serif text-3xl md:text-4xl mt-2">Kalite Yönetimi</h1>
          <p className="text-[#6b655c] mt-1 text-sm">Önce kalite oluşturun, sonra ürün eklerken kaliteyi seçin.</p>
        </div>
        <Button onClick={openCreate} className="btn-luxe text-xs" data-testid="admin-quality-new-btn">
          <Plus size={14} /> Yeni Kalite
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((q) => (
          <div key={q.id} className="bg-white border border-[#eae5dc] overflow-hidden" data-testid={`quality-card-${q.id}`}>
            {q.image_path && <img src={q.image_path} alt="" className="w-full aspect-video object-cover" />}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-xl truncate">{q.name}</h3>
                    {q.slug && <span className="text-[0.65rem] font-mono text-[#6b655c]">/{q.slug}</span>}
                  </div>
                  <p className="text-sm text-[#6b655c] mt-1 line-clamp-2">{q.short_description}</p>
                </div>
                <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-1 flex-shrink-0 ${q.active ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>
                  {q.active ? "Aktif" : "Pasif"}
                </span>
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-[#c8a97e] uppercase tracking-wider">{q.product_count} ürün</span>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(q)} className="p-2 hover:bg-[#f7f5f0]" data-testid={`quality-edit-${q.id}`}><Edit2 size={14} /></button>
                  <button onClick={() => setDelId(q.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`quality-del-${q.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#6b655c] col-span-full text-center py-12">Henüz kalite yok. Yeni kalite ekleyin.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit ? "Kaliteyi Düzenle" : "Yeni Kalite"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Kalite Adı *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Avangart" data-testid="quality-form-name" />
            </Field>
            <Field label="Kısa Açıklama">
              <Textarea rows={3} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
            </Field>
            <ImageUpload
              label="Görsel"
              value={form.image_path}
              onChange={(url) => setForm({ ...form, image_path: url })}
              testid="quality-form-image"
            />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sıra">
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </Field>
              <div className="flex items-center justify-between">
                <span className="text-sm">Aktif</span>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} className="btn-luxe text-xs" data-testid="quality-form-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaliteyi sil?</AlertDialogTitle>
            <AlertDialogDescription>İçinde ürün varsa silinemez. Önce ürünleri başka kaliteye taşıyın.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-1.5">{label}</label>
    {children}
  </div>
);
